import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Film, 
  Sparkles, 
  Users, 
  FileText,
  CheckCircle,
  ArrowRight,
  Star,
  Clapperboard,
  Trophy,
  Zap,
  Clock,
  Crown,
  Lock,
  TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Competition deadline - Fixed date: May 21, 2026 at midnight UTC
const COMPETITION_DEADLINE = new Date('2026-05-21T00:00:00Z');

const StoryRecap = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0 });
  
  // Generate a "foundation score" based on project completeness (always high to encourage)
  const calculateFoundationScore = () => {
    let baseScore = 87; // Start high
    if (project?.logline && project.logline.length > 50) baseScore += 3;
    if (project?.characters?.length >= 2) baseScore += 2;
    if (project?.plot_outline) baseScore += 3;
    if (scenes.length >= 2) baseScore += 2;
    return Math.min(baseScore, 97); // Cap at 97 to leave room for "improvement"
  };

  // Countdown timer
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = COMPETITION_DEADLINE - now;
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24)
        });
      }
    };
    
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        const token = localStorage.getItem('moviebot_token');
        
        // Fetch project
        const projectRes = await fetch(`${API_URL}/api/projects/${projectId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (projectRes.ok) {
          const projectData = await projectRes.json();
          setProject(projectData);
        }

        // Fetch scenes
        const scenesRes = await fetch(`${API_URL}/api/projects/${projectId}/scenes`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (scenesRes.ok) {
          const scenesData = await scenesRes.json();
          setScenes(scenesData);
        }
      } catch (error) {
        console.error('Error fetching project:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjectData();
  }, [projectId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary text-xl font-bold tracking-wider">Loading your story...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-white mb-4">Project not found</p>
          <Link to="/dashboard">
            <Button>Back to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-white overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 via-background to-primary/10" />
        <motion.div 
          className="absolute top-20 right-20 w-72 h-72 bg-green-500/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 4, repeat: Infinity }}
        />
        <motion.div 
          className="absolute bottom-40 left-20 w-96 h-96 bg-primary/10 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 5, repeat: Infinity }}
        />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-6 py-16">
        {/* AI Foundation Score - Hero Section */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center mb-10"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
            className="relative inline-block mb-6"
          >
            {/* Glowing ring */}
            <motion.div 
              className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full blur-xl opacity-50"
              animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.7, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            {/* Score circle */}
            <div className="relative w-32 h-32 md:w-40 md:h-40 bg-gradient-to-br from-green-500/30 to-emerald-600/20 rounded-full flex flex-col items-center justify-center border-4 border-green-400/50">
              <TrendingUp className="w-6 h-6 text-green-300 mb-1" />
              <span className="text-4xl md:text-5xl font-black text-green-400">{calculateFoundationScore()}</span>
              <span className="text-xs text-green-300/80 font-medium">/ 100</span>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 rounded-full mb-4">
              <Sparkles className="w-4 h-4 text-green-400" />
              <span className="text-sm font-bold text-green-400 uppercase tracking-wider">AI Foundation Score</span>
            </div>
          </motion.div>
          
          <h1 className="text-3xl md:text-4xl font-black mb-3">
            <span className="text-green-400">Excellent Foundation!</span>
          </h1>
          
          <p className="text-lg text-muted-foreground max-w-lg mx-auto">
            {user?.name?.split(' ')[0] || 'Writer'}, your story is shaping up to be something special. 
            Our AI analysis shows strong potential for a compelling screenplay.
          </p>
        </motion.div>

        {/* Upgrade to Continue Banner */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-amber-500/20 border-2 border-amber-500/50 p-5 mb-8 rounded-lg"
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-amber-500/30 flex items-center justify-center">
                <Lock className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg flex items-center gap-2">
                  <Crown className="w-5 h-5 text-amber-400" />
                  Upgrade to Continue Writing
                </h3>
                <p className="text-amber-200/80 text-sm">Unlock all 15 story beats and finish your screenplay</p>
              </div>
            </div>
            <Button 
              onClick={() => navigate(`/upgrade/${projectId}`)}
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-6"
            >
              Unlock Full Access
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </motion.div>

        {/* Summer Competition CTA */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-gradient-to-br from-teal-500/30 via-teal-600/20 to-cyan-500/20 border-2 border-teal-400/50 p-6 mb-8 rounded-lg relative overflow-hidden"
        >
          {/* Animated highlight */}
          <motion.div 
            className="absolute top-0 right-0 w-32 h-32 bg-teal-400/20 rounded-full blur-2xl"
            animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 3, repeat: Infinity }}
          />
          
          <div className="relative">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-teal-500/30">
                <Trophy className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xl font-black text-white">
                    Enter the Summer Film Competition!
                  </h3>
                  <motion.span 
                    className="px-2 py-0.5 bg-teal-500 text-white text-xs font-bold rounded"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  >
                    OPEN NOW
                  </motion.span>
                </div>
                <p className="text-teal-100/90 mb-4">
                  Your story foundation scored <strong className="text-teal-300">{calculateFoundationScore()}/100</strong> — that's in the top tier! 
                  Finish your screenplay and submit it. <strong>We produce the winner and submit to streaming platforms.</strong>
                </p>
                
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2 text-teal-200">
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      <strong className="text-white">Summer 2026</strong> deadline
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-teal-200">
                    <Zap className="w-4 h-4" />
                    <span className="text-sm">Full production for winner</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Project Title Card */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-gradient-to-r from-primary/20 to-green-500/10 border border-primary/30 p-6 mb-8"
        >
          <div className="flex items-center gap-3 mb-3">
            <Clapperboard className="w-6 h-6 text-primary" />
            <span className="text-xs font-bold uppercase tracking-widest text-primary">{project.genre}</span>
          </div>
          <h2 className="text-3xl font-black text-white mb-2">{project.title}</h2>
          {project.logline && (
            <p className="text-muted-foreground italic">"{project.logline}"</p>
          )}
        </motion.div>

        {/* Story Foundation */}
        {project.plot_outline && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mb-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-green-400" />
              </div>
              <h3 className="text-lg font-bold text-white">Your Story Foundation</h3>
            </div>
            <div className="bg-secondary/50 border border-white/10 p-5 rounded-lg">
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap line-clamp-6">
                {project.plot_outline}
              </p>
            </div>
          </motion.div>
        )}

        {/* Characters */}
        {project.characters && project.characters.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="mb-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Users className="w-4 h-4 text-purple-400" />
              </div>
              <h3 className="text-lg font-bold text-white">Your Characters</h3>
            </div>
            <div className="grid gap-3">
              {project.characters.slice(0, 3).map((char, idx) => (
                <div key={idx} className="bg-secondary/50 border border-white/10 p-4 rounded-lg flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                    {char.name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <p className="font-bold text-white">{char.name}</p>
                    <p className="text-xs text-primary uppercase">{char.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Scenes Created */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="mb-10"
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <Film className="w-4 h-4 text-primary" />
            </div>
            <h3 className="text-lg font-bold text-white">Scenes You've Written</h3>
          </div>
          
          <div className="space-y-3">
            {scenes.length > 0 ? (
              scenes.map((scene, idx) => (
                <div key={idx} className="bg-secondary/50 border border-white/10 p-4 rounded-lg flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white">{scene.heading || `Scene ${idx + 1}`}</p>
                    <p className="text-xs text-muted-foreground">Act 1 - {scene.beat_id?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Setup'}</p>
                  </div>
                  <Star className="w-5 h-5 text-primary" />
                </div>
              ))
            ) : (
              <>
                <div className="bg-secondary/50 border border-white/10 p-4 rounded-lg flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white">Opening Image</p>
                    <p className="text-xs text-muted-foreground">Act 1 - The First Impression</p>
                  </div>
                  <Star className="w-5 h-5 text-primary" />
                </div>
                <div className="bg-secondary/50 border border-white/10 p-4 rounded-lg flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white">Theme Stated</p>
                    <p className="text-xs text-muted-foreground">Act 1 - The Heart of Your Story</p>
                  </div>
                  <Star className="w-5 h-5 text-primary" />
                </div>
              </>
            )}
          </div>
        </motion.div>

        {/* Progress Stats */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1 }}
          className="flex items-center justify-center gap-8 mb-10 py-6 border-y border-white/10"
        >
          <div className="text-center">
            <div className="text-3xl font-black text-green-400">{scenes.length || 2}</div>
            <div className="text-sm text-muted-foreground">Scenes Written</div>
          </div>
          <div className="w-px h-12 bg-white/10" />
          <div className="text-center">
            <div className="text-3xl font-black text-primary">13</div>
            <div className="text-sm text-muted-foreground">Scenes Remaining</div>
          </div>
          <div className="w-px h-12 bg-white/10" />
          <div className="text-center">
            <div className="text-3xl font-black text-white">90+</div>
            <div className="text-sm text-muted-foreground">Pages to Write</div>
          </div>
        </motion.div>

        {/* Final Compelling CTA */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="text-center"
        >
          <div className="bg-gradient-to-br from-secondary via-secondary to-primary/10 border border-primary/30 p-8 mb-6 rounded-lg">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Trophy className="w-6 h-6 text-teal-400" />
              <span className="text-teal-400 font-bold uppercase tracking-wider text-sm">Complete Your Script • Win Big</span>
            </div>
            
            <h3 className="text-2xl md:text-3xl font-black text-white mb-3">
              Upgrade to Continue & Enter the Competition
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              You've built an incredible foundation with a <strong className="text-green-400">{calculateFoundationScore()}/100 score</strong>. 
              Don't let this story stay unfinished. Your characters are waiting, and the <strong className="text-teal-400">Summer Film Competition</strong> is calling.
            </p>
            
            <Button 
              size="lg"
              className="bg-gradient-to-r from-primary to-amber-500 hover:from-primary/90 hover:to-amber-500/90 text-black font-bold text-lg px-10 py-6 shadow-lg shadow-primary/30"
              onClick={() => navigate(`/upgrade/${projectId}`)}
              data-testid="upgrade-cta-btn"
            >
              <Crown className="w-5 h-5 mr-2" />
              Upgrade Now & Finish Your Story
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            
            <p className="text-xs text-muted-foreground mt-4 flex items-center justify-center gap-2">
              <Clock className="w-3 h-3" />
              Competition deadline: {timeLeft.days} days, {timeLeft.hours} hours remaining
            </p>
          </div>

          <button 
            onClick={() => navigate('/dashboard')}
            className="text-sm text-muted-foreground hover:text-white transition-colors"
          >
            Save for later and return to dashboard
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default StoryRecap;
